import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBackRounded, EmailRounded, LockResetRounded } from '@mui/icons-material';
import { requestProfessionalPasswordResetEmail } from '../../services/api/platformIntegrationService';
import { friendlyError } from '../../utils/userMessages';

const C = {
  ink: '#182235',
  muted: '#53677B',
  blue: '#2E6F8E',
  green: '#42B89B',
  line: 'rgba(31,62,82,.12)',
};

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const submit = async (event) => {
    event.preventDefault();
    const value = String(email || '').trim().toLowerCase();
    if (!value) {
      setError('Enter your account email address.');
      return;
    }
    setLoading(true);
    setError('');
    setMessage('');
    try {
      const response = await requestProfessionalPasswordResetEmail(value);
      setMessage(response?.message || 'If an account exists for that email, a reset link has been sent.');
    } catch (err) {
      setError(friendlyError(err, 'Unable to request a password reset right now.'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        py: 5,
        bgcolor: '#F2F7F9',
        backgroundImage: `
          radial-gradient(circle at 15% 18%, rgba(46,111,142,.14) 0, rgba(46,111,142,0) 27%),
          radial-gradient(circle at 85% 78%, rgba(66,184,155,.14) 0, rgba(66,184,155,0) 28%)
        `,
      }}
    >
      <Paper
        component="form"
        onSubmit={submit}
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 470,
          p: { xs: 2.6, sm: 4 },
          borderRadius: 5,
          border: `1px solid ${C.line}`,
          boxShadow: '0 28px 70px -38px rgba(24,34,53,.36)',
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            width: 180,
            height: 180,
            right: -72,
            top: -88,
            borderRadius: '50%',
            border: '28px solid rgba(46,111,142,.055)',
            pointerEvents: 'none',
          },
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 3.5,
              display: 'grid',
              placeItems: 'center',
              mb: 2.2,
              color: '#fff',
              background: `linear-gradient(135deg, ${C.blue}, ${C.green})`,
              boxShadow: '0 14px 30px -16px rgba(46,111,142,.9)',
            }}
          >
            <LockResetRounded />
          </Box>

          <Typography sx={{ color: C.ink, fontSize: '1.65rem', fontWeight: 900, letterSpacing: '-.035em' }}>
            Reset your password
          </Typography>
          <Typography sx={{ color: C.muted, fontSize: '.88rem', mt: .75, lineHeight: 1.65 }}>
            Enter your account email. If the account exists, TELCOFAIR will send the reset link through the professional company email configured by the administrator.
          </Typography>

          {message ? <Alert severity="success" sx={{ mt: 2.3, borderRadius: 2.5 }}>{message}</Alert> : null}
          {error ? <Alert severity="error" sx={{ mt: 2.3, borderRadius: 2.5 }}>{error}</Alert> : null}

          <TextField
            fullWidth
            required
            type="email"
            autoComplete="email"
            label="Email address"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            disabled={loading}
            InputProps={{ startAdornment: <EmailRounded sx={{ mr: 1, fontSize: 20, color: C.muted }} /> }}
            sx={{ mt: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#fff' } }}
          />

          <Button
            type="submit"
            fullWidth
            variant="contained"
            disabled={loading}
            startIcon={loading ? <CircularProgress color="inherit" size={17} /> : <LockResetRounded />}
            sx={{
              mt: 2,
              py: 1.35,
              borderRadius: 3,
              textTransform: 'none',
              fontWeight: 850,
              background: `linear-gradient(135deg, ${C.blue}, ${C.green})`,
            }}
          >
            {loading ? 'Sending...' : 'Send reset link'}
          </Button>

          <Button
            component={Link}
            to="/login"
            startIcon={<ArrowBackRounded />}
            sx={{ mt: 1.3, textTransform: 'none', fontWeight: 750, color: C.blue }}
          >
            Back to login
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
